import { DELETED_ACCOUNT_LABEL } from '@/domain/account-deletion';
import { placeholderKey, type Directory } from '@/domain/directory';
import { parseRef } from '@/domain/refs';
import { format, m, type Message, type Translator } from '@/i18n';

/**
 * The shared **dev/test** credential every seeded demo persona signs in with. Single
 * source of truth for the seeds (`scripts/seed.ts`, `scripts/seed-e2e.ts`) and the dev
 * persona switcher (`devSignInAs`). NOT a production secret: these accounts only exist in
 * seeded dev/test databases.
 */
export const DEMO_PASSWORD = 'demo-pass-1234';

/** The email a seeded demo persona signs in with (`"ari"` -> `"ari@demo.tesserano"`). */
export function demoEmail(personaId: string): string {
  return `${personaId}@demo.tesserano`;
}

/**
 * Resolves an actor ref to display info. This is the seam where a real persons
 * table (separate, deletable, PII-bearing) will plug in later; for now it reads a
 * fixed demo roster. The log and the model only ever hold the stable ref, never
 * this display data. Client-safe (no DB, no PII storage).
 */
export interface Actor {
  ref: string;
  name: string;
  initials: string;
  kind: 'person' | 'group' | 'automation';
  hue: number;
  /** The referenced entity can't be resolved (deleted, or not in the directory).
   *  The `name` is a placeholder, never the raw id; the UI can mute a missing actor. */
  missing?: boolean;
  /** The directory marks this person's account deleted or scrubbed
   *  (docs/MODEL.md §6b). The `name` still resolves: the stored one until the
   *  scrub, "Deleted account" after it. */
  deleted?: boolean;
}

/**
 * Display placeholders for a ref whose entity can't be resolved - it may be deleted,
 * or simply not in the directory / from another org. We only know resolution failed,
 * not why, so the copy is "Unknown ..." rather than asserting "Deleted". The log holds
 * stable ids only, and we **never** surface the raw id (a uuid); this stands in. One
 * source of truth, reused by the DB-backed group-name lookups too.
 */
export const MISSING_NAME: Record<Actor['kind'], Message> = {
  person: m('common.unknown.person'),
  group: m('common.unknown.group'),
  automation: m('common.unknown.automation'),
};

/**
 * A directory that also carries the placeholders in the reader's language
 * (`placeholderKey`, src/domain/directory.ts): what `getDirectory()` hands out,
 * and through the root layout `useDirectory()`, so every `resolveActor`
 * through them names a missing ref in the viewer's locale. A scrubbed person
 * (`scrubbed`, docs/MODEL.md §6b) is named the same way: their entry's stored
 * English is replaced by "Deleted account" in the reader's language. A
 * directory without this step (a script, the agent tools) falls back to
 * English.
 */
export function withPlaceholders(directory: Directory, t: Translator): Directory {
  const deletedName = t.msg(DELETED_ACCOUNT_LABEL);
  const named: Record<string, Directory[string]> = {};
  for (const [ref, entry] of Object.entries(directory)) {
    named[ref] = entry.scrubbed ? { ...entry, name: deletedName } : entry;
  }
  return {
    ...named,
    [placeholderKey('person')]: { name: t.msg(MISSING_NAME.person) },
    [placeholderKey('group')]: { name: t.msg(MISSING_NAME.group) },
    [placeholderKey('automation')]: { name: t.msg(MISSING_NAME.automation) },
  };
}

/** The placeholder name for a ref of this kind, in the directory's language. */
function placeholderName(kind: Actor['kind'], directory?: Directory): string {
  return directory?.[placeholderKey(kind)]?.name ?? format('en', MISSING_NAME[kind]);
}

// Demo people, deliberately gender-neutral and alphabetical (Ari=A, Blake=B, ...)
// so examples never lean on gendered or ordered assumptions.
const ROSTER: Record<string, { name: string; hue: number }> = {
  'person:ari': { name: 'Ari', hue: 172 },
  'person:blake': { name: 'Blake', hue: 32 },
  'person:chris': { name: 'Chris', hue: 280 },
  'person:danny': { name: 'Danny', hue: 210 },
  'person:elliot': { name: 'Elliot', hue: 338 },
  'group:platform': { name: 'Platform', hue: 196 },
  'group:design': { name: 'Design', hue: 312 },
};

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** A ref's kind; a ref that does not parse is named as a person's. */
function kindOf(ref: string): Actor['kind'] {
  try {
    return parseRef(ref).kind;
  } catch {
    return 'person';
  }
}

/** An automation's slug: stable and readable (not PII, not a uuid), so it
 *  names the automation. */
function automationSlug(ref: string): string {
  return ref.slice(ref.indexOf(':') + 1);
}

/**
 * The words for a ref that no directory entry names, on a surface that holds
 * names as a list rather than a `Directory` (the filter chips,
 * `SuggestContext.actors`): an automation's slug, else the placeholder of the
 * ref's kind ("Unknown user", "Unknown group") as a descriptor the surface
 * formats in the reader's language. The same words `resolveActor` gives such
 * a ref through a directory; never the raw ref.
 */
export function unknownActorName(ref: string): Message | string {
  const kind = kindOf(ref);
  return kind === 'automation' ? automationSlug(ref) : MISSING_NAME[kind];
}

export function resolveActor(ref: string, directory?: Directory): Actor {
  // The loaded directory (src/read/directory.ts, viewer-scoped) wins; the static
  // roster below is the dev cast and the fallback when no directory reached the
  // caller. A name the viewer may not see is simply absent from the directory,
  // so it falls through to the missing placeholder - never to the raw id.
  const loaded = directory?.[ref];
  if (loaded) {
    const actor = actorWithName(ref, loaded.name);
    return loaded.deleted ? { ...actor, deleted: true } : actor;
  }
  const known = ROSTER[ref];
  const kind = kindOf(ref);
  if (known) {
    return { ref, name: known.name, initials: initialsOf(known.name), kind, hue: known.hue };
  }
  if (kind === 'automation') {
    const slug = automationSlug(ref);
    return { ref, name: slug, initials: slug.slice(0, 2).toUpperCase(), kind, hue: 220 };
  }
  // A person/group ref we cannot resolve (deleted, or not in the directory). NEVER
  // surface the raw id - stand in a placeholder and mark it missing so the UI can mute it.
  return {
    ref,
    name: placeholderName(kind, directory),
    initials: '?',
    kind,
    hue: 220,
    missing: true,
  };
}

/**
 * "asked Ari" - the quiet note a gesture that became an ASK leaves on the line or
 * the page (docs/RESOLUTION.md §12 step 8: the propose lane landed as a proposal
 * to the item's owners). Named through the viewer's directory; an owner the
 * viewer may not see by name is never guessed at, and the seat speaks instead.
 * Pure.
 */
export function askedNote(refs: readonly string[], directory: Directory): Message {
  const names = refs.map((r) => directory[r]?.name).filter((n): n is string => !!n);
  return names.length > 0 ? m('common.asked.names', { names }) : m('common.asked.requester');
}

/** A stable hue (0-359) derived from a ref, so a group not in the demo roster still
 *  gets a consistent colour instead of a single shared fallback. */
function hueForRef(ref: string): number {
  let h = 0;
  for (let i = 0; i < ref.length; i++) h = (h * 31 + ref.charCodeAt(i)) % 360;
  return h;
}

/**
 * Build a display Actor for a ref whose real name is known from elsewhere (e.g. a
 * group name resolved from the DB), so a group/person that isn't in the demo roster
 * still renders with its true name and a stable colour instead of the "Unknown"
 * placeholder. Used where the caller has the name in hand (the members panel resolves
 * group names from the org's group list).
 */
export function actorWithName(ref: string, name: string): Actor {
  const base = resolveActor(ref);
  return {
    ...base,
    name,
    initials: initialsOf(name),
    hue: base.missing ? hueForRef(ref) : base.hue,
    missing: false,
  };
}

/** Is this a demo persona id (`ari`..`elliot`)? The membership test behind the
 *  dev quick-switch: it may sign in as the demo cast and as nobody else, so a
 *  real account can never be reached through a development affordance. */
export function isDemoPersona(personaId: string): boolean {
  return roster().some((actor) => actor.ref === `person:${personaId}`);
}

/**
 * Whom the dev persona switcher may sign in as (`devSignInAs`, src/app/actions.ts):
 * the demo persona's bare id, or why not. TWO bounds (2026-09-06): never in
 * production, and the demo cast only, so the switcher can never reach a real
 * account. Takes a bare id (`ari`) or an encoded ref (`person:ari`). Pure, so
 * both bounds are unit-tested in every gate: the switcher's UI exists only
 * under a dev server, which no gate runs.
 */
export function devSignInTarget(
  nodeEnv: string | undefined,
  personaId: string,
): { ok: true; id: string } | { ok: false; reason: string } {
  if (nodeEnv === 'production') {
    return {
      ok: false,
      reason: 'devSignInAs is a development-only testing aid and is disabled in production.',
    };
  }
  // A bare id, or a PERSON ref; any other ref kind names no persona.
  const id = personaId.startsWith('person:')
    ? personaId.slice('person:'.length)
    : personaId.includes(':')
      ? null
      : personaId;
  if (id !== null && isDemoPersona(id)) return { ok: true, id };
  return {
    ok: false,
    reason: `devSignInAs accepts demo personas only; "${personaId}" is not one.`,
  };
}

/** The demo cast for the "act as" switcher (people only). */
export function roster(): Actor[] {
  return Object.keys(ROSTER)
    .filter((ref) => ref.startsWith('person:'))
    .map((ref) => resolveActor(ref));
}

/**
 * **Whom this viewer may PICK**: the refs in their loaded directory
 * (`src/read/directory.ts`), sorted by name.
 *
 * The candidate list and the name list are the same set on purpose (ruled
 * 2026-09-12). Until now every picker was fed by `roster()`, the seeded demo
 * cast, so on a real store two accounts could not seat, grant, or add each
 * other at all while five phantom personas were offered instead. The directory
 * is already the viewer-scoped answer to "whose name may reach you" (yourself,
 * members of your groups, parties and grantees on items you can see - never
 * bare org standing, docs/COMMONS.md), and offering a name you may not see is
 * the same leak as rendering one.
 *
 * Reaching someone OUTSIDE that set is a separate, deliberate act: an item
 * invite by address, or an account link carrying a group seat
 * (`src/server/account-invites.ts`). A picker never browses the platform.
 *
 * Pure and client-safe: the caller supplies the directory (`useDirectory()` in
 * client components, `getDirectory()` on the server), so there is one source
 * for names and candidates.
 */
export function pickableRefs(
  directory: Directory,
  opts?: { kind?: Actor['kind']; exclude?: Iterable<string> },
): string[] {
  const exclude = new Set(opts?.exclude ?? []);
  return Object.keys(directory)
    .filter((ref) => {
      if (exclude.has(ref)) return false;
      // A placeholder entry is no ref (`placeholderKey`).
      if (ref.startsWith('?')) return false;
      // A deleted account keeps its name on what already shows it, and is
      // offered to nobody (docs/MODEL.md §6b).
      if (directory[ref]?.deleted) return false;
      let kind: Actor['kind'];
      try {
        kind = parseRef(ref).kind;
      } catch {
        return false;
      }
      // Automations are never pickable parties: a machine acts with a person's
      // authority (src/server/machine-keys.ts), it never holds a seat itself.
      if (kind === 'automation') return false;
      return opts?.kind ? kind === opts.kind : true;
    })
    .sort((a, b) => (directory[a]?.name ?? '').localeCompare(directory[b]?.name ?? ''));
}

/** {@link pickableRefs} as `{ ref, name }` pairs, the shape the filter/search
 *  suggest contexts take (`SuggestContext.actors`). Same set, same rule. */
export function pickableActors(
  directory: Directory,
  opts?: { kind?: Actor['kind']; exclude?: Iterable<string> },
): { ref: string; name: string }[] {
  return pickableRefs(directory, opts).map((ref) => ({
    ref,
    name: directory[ref]?.name ?? placeholderName(parseRef(ref).kind, directory),
  }));
}
